/**
 * Product bundles (Kaching-style UI).
 * Real checkout discounts come from Shopify Admin automatic discounts / codes / multipack variants.
 */
(function () {
  const activeCurrency = () =>
    window.Shopify?.currency?.active || window.theme?.cartCurrency || window.theme?.shopCurrency || 'USD';

  const activeLocale = () =>
    String(window.Shopify?.locale || window.theme?.locale || 'en').replace('_', '-');

  const toPresentmentCents = (shopCents, shopCurrency) => {
    if (typeof window.theme?.toPresentmentCents === 'function') {
      return window.theme.toPresentmentCents(shopCents);
    }
    const amount = Number(shopCents) || 0;
    const shop = shopCurrency || window.theme?.shopCurrency;
    const active = activeCurrency();
    if (!amount) return 0;
    if (shop && active && shop === active) return amount;
    const rate = Number(window.Shopify?.currency?.rate);
    if (!rate || Number.isNaN(rate)) return amount;
    return Math.round(amount * rate);
  };

  /**
   * Native Admin percentage discounts: discount is calculated per unit,
   * rounded down to the cent, then multiplied by quantity.
   * (Shopify, 2026 — calculating on the line total is off by 1–2 cents.)
   */
  const shopifyPercentOffCents = (unitCents, percent) => {
    const price = Number(unitCents) || 0;
    const pct = Number(percent) || 0;
    if (price <= 0 || pct <= 0) return 0;
    return Math.floor((price * pct) / 100);
  };

  const moneyFormat = (cents) => {
    if (typeof window.theme?.formatMoney === 'function') {
      return window.theme.formatMoney(cents);
    }
    try {
      return new Intl.NumberFormat(activeLocale(), {
        style: 'currency',
        currency: activeCurrency(),
      }).format((Number(cents) || 0) / 100);
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || undefined);
      }
    } catch (e) {}
    return ((Number(cents) || 0) / 100).toFixed(2);
  };

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  class ProductBundles extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-pb-config]').textContent);
      } catch (e) {
        console.error('[product-bundles] invalid config', e);
        return;
      }

      this.productForm =
        document.querySelector(`#${this.config.productFormId}`) ||
        document.querySelector('product-form form') ||
        document.querySelector('form[action*="/cart/add"]');

      this.selectedTierIndex = this.config.defaultTierIndex || 0;
      this.itemSelections = [];
      this.cacheShopPricesIfPrimaryMarket();
      this.bindQuantityBreaks();
      this.bindFbt();
      this.bindBxgy();
      this.bindMixMatch();
      this.interceptSubmit();
      this.syncQuantityField();
      this.renderItemVariantPickers();
      this.updateDisplays();
      this.toggleNativeQuantity();
      this.emitQuantityChange();
    }

    get unitPrice() {
      const idInput = this.productForm?.querySelector('[name="id"]');
      const variantId = idInput ? Number(idInput.value) : this.config.variantId;
      const variant = (this.config.variants || []).find((v) => Number(v.id) === Number(variantId));
      return variant ? variant.price : this.config.price;
    }

    shopPriceCacheKey() {
      return `pb:shopPrices:${this.config.shopCurrency || window.theme?.shopCurrency || 'shop'}`;
    }

    cacheShopPricesIfPrimaryMarket() {
      const shop = this.config.shopCurrency || window.theme?.shopCurrency;
      const active = activeCurrency();
      if (!shop || shop !== active) return;
      try {
        const map = {};
        (this.config.variants || []).forEach((v) => {
          if (v?.id && Number(v.price) > 0) map[String(v.id)] = Number(v.price);
        });
        if (Object.keys(map).length) localStorage.setItem(this.shopPriceCacheKey(), JSON.stringify(map));
      } catch (e) {}
    }

    readCachedShopPrice(variantId) {
      try {
        const map = JSON.parse(localStorage.getItem(this.shopPriceCacheKey()) || '{}');
        return Number(map[String(variantId)]) || 0;
      } catch (e) {
        return 0;
      }
    }

    readAnalyticsShopPrice(variantId, presentmentPrice) {
      const variants = window.ShopifyAnalytics?.meta?.product?.variants;
      if (!Array.isArray(variants)) return 0;
      const found = variants.find((item) => Number(item.id) === Number(variantId));
      if (found?.price == null) return 0;
      let cents = Number(found.price);
      if (!cents || Number.isNaN(cents)) return 0;
      if (!Number.isInteger(cents)) cents = Math.round(cents * 100);
      if (cents === Number(presentmentPrice)) return 0;
      return cents;
    }

    getShopCurrencyUnitPrice(unitPrice) {
      const variantId = this.getCurrentFormVariantId();
      const variant = this.getVariantById(variantId);
      if (Number(variant?.shopPrice) > 0) return Number(variant.shopPrice);

      if (Number(this.config.storeCurrencyPrice) > 0) return Number(this.config.storeCurrencyPrice);

      const shop = this.config.shopCurrency || window.theme?.shopCurrency;
      const active = activeCurrency();
      if (shop && active && shop === active && Number(unitPrice) > 0) return Number(unitPrice);

      const cached = this.readCachedShopPrice(variantId);
      if (cached > 0) return cached;

      const analyticsPrice = this.readAnalyticsShopPrice(variantId, unitPrice);
      if (analyticsPrice > 0) return analyticsPrice;

      return 0;
    }

    getSelectedTier() {
      const tiers = this.config.tiers || [];
      return tiers[this.selectedTierIndex] || tiers[0];
    }

    getCurrentFormVariantId() {
      const idInput = this.productForm?.querySelector('[name="id"]');
      return Number(idInput?.value || this.config.variantId);
    }

    getVariantById(id) {
      return (this.config.variants || []).find((v) => Number(v.id) === Number(id));
    }

    isVariantPurchasable(variant) {
      if (!variant) return false;
      // Shopify: when inventory is not tracked, the variant can still be bought.
      if (!variant.inventory_management) return true;
      if (variant.inventory_policy === 'continue') return true;
      return variant.available === true;
    }

    getPackQuantity() {
      if (this.config.bundleType === 'bxgy') {
        return Number(this.config.bxgy?.buyQty || 2) + Number(this.config.bxgy?.getQty || 1);
      }
      if (this.config.bundleType === 'quantity_break') {
        return Number(this.getSelectedTier()?.quantity) || 1;
      }
      return 1;
    }

    usesPerItemVariants() {
      if (!this.config.enablePerItemVariants) return false;
      if (this.config.hasOnlyDefaultVariant) return false;
      if (this.config.discountMode === 'multipack_variants') return false;
      return this.config.bundleType === 'quantity_break' || this.config.bundleType === 'bxgy';
    }

    findVariantForOptions(optionValues) {
      const variants = this.config.variants || [];
      return (
        variants.find((v) => {
          return (
            (optionValues[0] == null || v.option1 === optionValues[0]) &&
            (optionValues[1] == null || v.option2 === optionValues[1]) &&
            (optionValues[2] == null || v.option3 === optionValues[2])
          );
        }) || null
      );
    }

    getOptionValuesFromVariant(variant) {
      if (!variant) return [];
      return [variant.option1, variant.option2, variant.option3].filter((v) => v != null && v !== '');
    }

    bindQuantityBreaks() {
      if (this.config.bundleType !== 'quantity_break') return;
      this.querySelectorAll('[data-pb-tier]').forEach((el) => {
        const selectTier = () => {
          this.selectedTierIndex = Number(el.dataset.pbTier);
          this.querySelectorAll('[data-pb-tier]').forEach((t) => {
            t.classList.remove('is-selected');
            t.setAttribute('aria-checked', 'false');
          });
          el.classList.add('is-selected');
          el.setAttribute('aria-checked', 'true');
          this.syncQuantityField();
          this.applyMultipackVariant();
          this.renderItemVariantPickers();
          this.updateDisplays();
          this.emitQuantityChange();
        };
        el.addEventListener('click', selectTier);
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectTier();
          }
        });
      });
    }

    getStickyItems() {
      const type = this.config.bundleType;
      const fallbackVariant = this.getVariantById(this.getCurrentFormVariantId());
      const fallbackImage = fallbackVariant?.featured_image || null;
      const itemLabel = this.config.itemLabel || 'Item [n]';
      const items = [];

      if (
        (type === 'quantity_break' || type === 'bxgy') &&
        this.usesPerItemVariants() &&
        this.getPackQuantity() >= 2 &&
        this.itemSelections?.length
      ) {
        const optionDefs = this.config.options || [];
        this.itemSelections.forEach((sel, i) => {
          const variant = this.getVariantById(sel?.variantId);
          const selectedOptions = (sel?.options || []).filter((v) => v != null && v !== '');
          items.push({
            index: i,
            label: itemLabel.replaceAll('[n]', String(i + 1)),
            optionLabel: selectedOptions.join(' / ') || (variant?.title !== 'Default Title' ? variant?.title : '') || '',
            image: variant?.featured_image || fallbackImage,
            variantId: sel?.variantId || null,
            editable: true,
            optionFields: optionDefs.map((opt, optionIndex) => ({
              name: opt.name,
              index: optionIndex,
              values: opt.values || [],
              selected: sel?.options?.[optionIndex] || variant?.[`option${optionIndex + 1}`] || '',
            })),
          });
        });
        return items;
      }

      if (type === 'quantity_break' || type === 'bxgy') {
        const qty = this.getPackQuantity();
        for (let i = 0; i < Math.min(qty, 6); i += 1) {
          items.push({
            index: i,
            label: itemLabel.replaceAll('[n]', String(i + 1)),
            optionLabel:
              fallbackVariant?.title && fallbackVariant.title !== 'Default Title'
                ? fallbackVariant.title
                : '',
            image: fallbackImage,
            variantId: fallbackVariant?.id || null,
            editable: false,
            optionFields: [],
          });
        }
        return items;
      }

      if (type === 'fbt') {
        const main = this.querySelector('[data-pb-fbt-main]');
        if (main) {
          items.push({
            index: -1,
            label: (main.querySelector('.pb-fbt__title')?.textContent || '').trim() || 'Product',
            optionLabel: '',
            image: main.querySelector('img')?.src || fallbackImage,
            variantId: this.getCurrentFormVariantId(),
            editable: false,
            optionFields: [],
          });
        }
        this.querySelectorAll('[data-pb-fbt-item]').forEach((el, i) => {
          const check = el.querySelector('[data-pb-fbt-check]');
          if (check && !check.checked) return;
          const select = el.querySelector('[data-pb-fbt-variant]');
          items.push({
            index: i,
            label: (el.querySelector('.pb-fbt__title')?.textContent || '').trim() || `Item ${i + 1}`,
            optionLabel: select?.selectedOptions?.[0]?.textContent?.split('—')?.[0]?.trim() || '',
            image: el.querySelector('img')?.src || null,
            variantId: Number(el.dataset.variantId) || null,
            editable: false,
            optionFields: [],
          });
        });
        return items;
      }

      if (type === 'mix_match') {
        this.querySelectorAll('[data-pb-mix-item]').forEach((el, i) => {
          const check = el.querySelector('[data-pb-mix-check]');
          if (check && !check.checked) return;
          const select = el.querySelector('[data-pb-mix-variant]');
          items.push({
            index: i,
            label: (el.querySelector('.pb-mix__title')?.textContent || '').trim() || `Item ${i + 1}`,
            optionLabel: select?.selectedOptions?.[0]?.textContent?.split('—')?.[0]?.trim() || '',
            image: el.querySelector('img')?.src || null,
            variantId: Number(el.dataset.variantId) || null,
            editable: false,
            optionFields: [],
          });
        });
        return items;
      }

      return items;
    }

    getStickySummary() {
      const type = this.config.bundleType;
      if (!type) return null;

      const textOrNull = (el) => {
        if (!el || el.hidden) return null;
        const text = (el.textContent || '').trim();
        return text || null;
      };

      const items = this.getStickyItems();
      const hasEditableItems = items.some((item) => item.editable);

      if (type === 'quantity_break') {
        const tierEl = this.querySelector('.pb-tier.is-selected');
        if (!tierEl) return null;
        const tier = this.getSelectedTier();
        const qty = Number(tier?.quantity) || 1;
        return {
          active: true,
          bundleType: type,
          quantity: qty,
          label: textOrNull(tierEl.querySelector('.pb-tier__title')),
          priceText: textOrNull(tierEl.querySelector('[data-pb-tier-price]')),
          compareText: textOrNull(tierEl.querySelector('[data-pb-tier-compare]')),
          saveText: textOrNull(tierEl.querySelector('[data-pb-tier-save]')),
          hideQuantity: !!this.config.hideQuantitySelector,
          hideStickyVariants: hasEditableItems,
          canEditItems: hasEditableItems,
          items,
        };
      }

      if (type === 'bxgy') {
        const buy = Number(this.config.bxgy?.buyQty || 2);
        const get = Number(this.config.bxgy?.getQty || 1);
        return {
          active: true,
          bundleType: type,
          quantity: buy + get,
          label: textOrNull(this.querySelector('.pb-bxgy__offer')) || `Buy ${buy} Get ${get}`,
          priceText: textOrNull(this.querySelector('[data-pb-total]')),
          compareText: null,
          saveText: textOrNull(this.querySelector('[data-pb-total-save]')),
          hideQuantity: true,
          hideStickyVariants: hasEditableItems,
          canEditItems: hasEditableItems,
          items,
        };
      }

      if (type === 'fbt' || type === 'mix_match') {
        const checked = this.querySelectorAll(
          type === 'fbt' ? '[data-pb-fbt-item] input[type="checkbox"]:checked' : '[data-pb-mix-item] input[type="checkbox"]:checked'
        ).length;
        const labelEl = this.querySelector('.pb-totals__label');
        return {
          active: true,
          bundleType: type,
          quantity: type === 'fbt' ? checked + 1 : checked,
          label: textOrNull(labelEl) || (type === 'fbt' ? 'Bundle' : 'Mix & match'),
          priceText: textOrNull(this.querySelector('[data-pb-total]')),
          compareText: null,
          saveText: textOrNull(this.querySelector('[data-pb-total-save]')),
          hideQuantity: true,
          hideStickyVariants: false,
          canEditItems: false,
          items,
        };
      }

      return null;
    }

    scrollToItemVariants() {
      const host =
        this.querySelector('.pb-tier.is-selected [data-pb-tier-colors]:not([hidden])') ||
        this.querySelector('[data-pb-item-variants]:not([hidden])') ||
        this.querySelector('.pb-fbt') ||
        this.querySelector('.pb-mix') ||
        this.querySelector('.pb-tiers') ||
        this;
      host.scrollIntoView({ behavior: 'smooth', block: 'center' });
      host.classList.add('pb-focus-flash');
      window.setTimeout(() => host.classList.remove('pb-focus-flash'), 1400);
    }

    setItemOptionFromSticky(itemIndex, optionIndex, value) {
      const row = this.querySelector(`[data-pb-item-index="${itemIndex}"]`);
      const select = row?.querySelector(`[data-pb-item-option="${optionIndex}"]`);
      if (!select) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    emitSelectionChange() {
      const summary = this.getStickySummary();
      document.dispatchEvent(
        new CustomEvent('pb:selection-change', {
          detail: { summary, bundleType: this.config.bundleType },
        })
      );
    }

    emitQuantityChange() {
      const tier = this.getSelectedTier();
      const quantity =
        this.config.bundleType === 'bxgy'
          ? Number(this.config.bxgy?.buyQty || 2) + Number(this.config.bxgy?.getQty || 1)
          : Number(tier?.quantity) || 1;
      document.dispatchEvent(
        new CustomEvent('pb:quantity-change', {
          detail: {
            quantity,
            tier,
            bundleType: this.config.bundleType,
            summary: this.getStickySummary(),
          },
        })
      );
      this.emitSelectionChange();
    }

    bindFbt() {
      if (this.config.bundleType !== 'fbt') return;
      this.querySelectorAll('[data-pb-fbt-item]').forEach((el) => {
        const check = el.querySelector('[data-pb-fbt-check]');
        const select = el.querySelector('[data-pb-fbt-variant]');
        const syncPrice = () => {
          if (!select) return;
          const opt = select.selectedOptions[0];
          if (opt?.dataset.price) el.dataset.price = opt.dataset.price;
          el.dataset.variantId = select.value;
        };
        const sync = () => {
          el.classList.toggle('is-checked', check.checked);
          syncPrice();
          this.updateDisplays();
        };
        check?.addEventListener('change', sync);
        select?.addEventListener('change', sync);
        sync();
      });
    }

    bindBxgy() {
      if (this.config.bundleType !== 'bxgy') return;
      const buyQty = Number(this.config.bxgy?.buyQty || 2);
      this.setFormQuantity(buyQty + Number(this.config.bxgy?.getQty || 1));
      this.renderItemVariantPickers();
    }

    bindMixMatch() {
      if (this.config.bundleType !== 'mix_match') return;
      this.querySelectorAll('[data-pb-mix-item]').forEach((el) => {
        const check = el.querySelector('[data-pb-mix-check]');
        const select = el.querySelector('[data-pb-mix-variant]');
        const syncPrice = () => {
          if (!select) return;
          const opt = select.selectedOptions[0];
          if (opt?.dataset.price) el.dataset.price = opt.dataset.price;
          el.dataset.variantId = select.value;
        };
        const sync = () => {
          el.classList.toggle('is-checked', check.checked);
          syncPrice();
          this.updateDisplays();
        };
        check?.addEventListener('change', sync);
        select?.addEventListener('change', sync);
        sync();
      });
    }

    renderItemVariantPickers() {
      this.querySelectorAll('[data-pb-tier-colors]').forEach((slot) => {
        slot.hidden = true;
        slot.innerHTML = '';
        slot.closest('.pb-tier')?.classList.remove('has-colors');
      });

      const legacyWrap = this.querySelector('[data-pb-item-variants]');
      if (legacyWrap) {
        legacyWrap.hidden = true;
        const legacyList = legacyWrap.querySelector('[data-pb-item-variants-list]');
        if (legacyList) legacyList.innerHTML = '';
      }

      const qty = this.getPackQuantity();
      if (!this.usesPerItemVariants() || qty < 2) {
        this.itemSelections = [];
        return;
      }

      let host = null;
      if (this.config.bundleType === 'quantity_break') {
        host = this.querySelector('.pb-tier.is-selected [data-pb-tier-colors]');
      } else if (legacyWrap) {
        host = legacyWrap.querySelector('[data-pb-item-variants-list]') || legacyWrap;
        legacyWrap.hidden = false;
      }
      if (!host) return;

      const options = this.config.options || [];
      const primaryOptionIndex = this.getPrimaryOptionIndex();
      const primaryOption = options[primaryOptionIndex] || options[0];
      const headingText =
        this.config.itemVariantHeading ||
        primaryOption?.name ||
        'Colors';

      const defaultVariant =
        this.getVariantById(this.getCurrentFormVariantId()) ||
        (this.config.variants || []).find((v) => this.isVariantPurchasable(v)) ||
        (this.config.variants || [])[0];
      const prev = this.itemSelections || [];
      const next = [];

      const heading = document.createElement('p');
      heading.className = 'pb-tier__colors-label';
      heading.textContent = headingText;

      const list = document.createElement('div');
      list.className = 'pb-tier__colors-list';

      for (let i = 0; i < qty; i++) {
        const existing = prev[i];
        const variant =
          (existing && this.getVariantById(existing.variantId)) || defaultVariant;
        const optionValues = this.getOptionValuesFromVariant(variant);
        next.push({
          variantId: Number(variant?.id || this.config.variantId),
          options: optionValues.slice(),
        });

        const row = document.createElement('div');
        row.className = 'pb-item-variant';
        row.dataset.pbItemIndex = String(i);

        const thumb = document.createElement('div');
        thumb.className = 'pb-item-variant__thumb';
        row.appendChild(thumb);

        const picker = document.createElement('div');
        picker.className = 'pb-item-variant__picker';

        const swatch = document.createElement('span');
        swatch.className = 'pb-item-variant__swatch';
        swatch.dataset.pbItemSwatch = '';
        picker.appendChild(swatch);

        const select = document.createElement('select');
        select.className = 'pb-item-variant__select';
        select.dataset.pbItemOption = String(primaryOptionIndex);
        select.setAttribute(
          'aria-label',
          `${headingText} ${i + 1}`
        );

        (primaryOption?.values || []).forEach((value) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value;
          if (optionValues[primaryOptionIndex] === value) opt.selected = true;
          select.appendChild(opt);
        });

        select.addEventListener('click', (e) => e.stopPropagation());
        select.addEventListener('mousedown', (e) => e.stopPropagation());
        select.addEventListener('change', () => this.onItemOptionChange(i));
        picker.appendChild(select);
        row.appendChild(picker);

        // Extra options (e.g. Size) as compact selects
        options.forEach((option, optionIndex) => {
          if (optionIndex === primaryOptionIndex) return;
          const extra = document.createElement('select');
          extra.className = 'pb-item-variant__select pb-item-variant__select--extra';
          extra.dataset.pbItemOption = String(optionIndex);
          extra.setAttribute('aria-label', `${option.name} ${i + 1}`);
          (option.values || []).forEach((value) => {
            const opt = document.createElement('option');
            opt.value = value;
            opt.textContent = value;
            if (optionValues[optionIndex] === value) opt.selected = true;
            extra.appendChild(opt);
          });
          extra.addEventListener('click', (e) => e.stopPropagation());
          extra.addEventListener('mousedown', (e) => e.stopPropagation());
          extra.addEventListener('change', () => this.onItemOptionChange(i));
          row.appendChild(extra);
        });

        const status = document.createElement('div');
        status.className = 'pb-item-variant__status';
        status.dataset.pbItemStatus = '';
        status.hidden = true;
        row.appendChild(status);

        list.appendChild(row);
      }

      host.innerHTML = '';
      host.appendChild(heading);
      host.appendChild(list);
      host.hidden = false;
      host.closest('.pb-tier')?.classList.add('has-colors');
      host.onclick = (e) => e.stopPropagation();

      this.itemSelections = next;
      next.forEach((sel, i) => this.syncItemRowUi(i, sel.variantId));
    }

    getPrimaryOptionIndex() {
      const options = this.config.options || [];
      const colorRe = /color|colour|cor|couleur|farbe|colore|色|カラー/i;
      const idx = options.findIndex((o) => colorRe.test(o?.name || ''));
      return idx >= 0 ? idx : 0;
    }

    getSwatchStyle(value, variant, optionIndex) {
      const option = this.config.options?.[optionIndex ?? this.getPrimaryOptionIndex()];
      const swatch = option?.swatches?.[value];
      if (swatch?.image) {
        return {
          backgroundImage: `url(${swatch.image})`,
          backgroundColor: swatch.color || '#f3f4f6',
        };
      }
      if (swatch?.color) {
        return {
          backgroundImage: 'none',
          backgroundColor: swatch.color,
        };
      }
      if (variant?.featured_image) {
        return {
          backgroundImage: `url(${variant.featured_image})`,
          backgroundColor: '#f3f4f6',
        };
      }
      const key = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
      const map = {
        white: '#ffffff',
        branco: '#ffffff',
        preto: '#111111',
        black: '#111111',
        red: '#dc2626',
        vermelho: '#dc2626',
        blue: '#2563eb',
        azul: '#2563eb',
        green: '#16a34a',
        verde: '#16a34a',
        yellow: '#eab308',
        amarelo: '#eab308',
        pink: '#ec4899',
        rosa: '#ec4899',
        purple: '#7c3aed',
        roxo: '#7c3aed',
        orange: '#f97316',
        laranja: '#f97316',
        brown: '#92400e',
        marrom: '#92400e',
        gray: '#9ca3af',
        grey: '#9ca3af',
        cinza: '#9ca3af',
        beige: '#d6c3a5',
        bege: '#d6c3a5',
        navy: '#1e3a8a',
      };
      return {
        backgroundImage: 'none',
        backgroundColor: map[key] || '#d1d5db',
      };
    }

    onItemOptionChange(index) {
      const row = this.querySelector(`[data-pb-item-index="${index}"]`);
      if (!row) return;

      const options = this.config.options || [];
      const optionValues = options.map((_, optionIndex) => {
        const select = row.querySelector(`[data-pb-item-option="${optionIndex}"]`);
        if (select) return select.value;
        return this.itemSelections?.[index]?.options?.[optionIndex] || null;
      });

      const matched = this.findVariantForOptions(optionValues);
      if (!this.itemSelections[index]) {
        this.itemSelections[index] = { variantId: this.config.variantId, options: optionValues };
      }
      this.itemSelections[index].options = optionValues;

      if (matched) {
        this.itemSelections[index].variantId = Number(matched.id);
        this.syncItemRowUi(index, matched.id);
      } else {
        this.itemSelections[index].variantId = null;
        this.syncItemRowUi(index, null);
      }
      this.emitSelectionChange();
    }

    syncItemRowUi(index, variantId) {
      const row = this.querySelector(`[data-pb-item-index="${index}"]`);
      if (!row) return;
      const variant = variantId ? this.getVariantById(variantId) : null;
      const thumb = row.querySelector('.pb-item-variant__thumb');
      const swatch = row.querySelector('[data-pb-item-swatch]');
      const status = row.querySelector('[data-pb-item-status]');
      const primaryOptionIndex = this.getPrimaryOptionIndex();
      const primarySelect = row.querySelector(`[data-pb-item-option="${primaryOptionIndex}"]`);
      const selectedValue = primarySelect?.value || variant?.[`option${primaryOptionIndex + 1}`];

      if (thumb) {
        thumb.innerHTML = '';
        const imageSrc =
          variant?.featured_image ||
          this.findVariantImageForOption(primaryOptionIndex, selectedValue);
        if (imageSrc) {
          const img = document.createElement('img');
          img.src = imageSrc;
          img.alt = variant?.title || selectedValue || '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          thumb.appendChild(img);
        }
      }

      if (swatch) {
        const style = this.getSwatchStyle(selectedValue, variant, primaryOptionIndex);
        swatch.style.backgroundImage = style.backgroundImage;
        swatch.style.backgroundColor = style.backgroundColor;
        swatch.style.backgroundSize = 'cover';
        swatch.style.backgroundPosition = 'center';
        const isLight =
          style.backgroundColor === '#ffffff' ||
          style.backgroundColor === '#fff' ||
          /white|branco/i.test(selectedValue || '');
        swatch.classList.toggle('is-light', Boolean(isLight && style.backgroundImage === 'none'));
      }

      if (status) {
        if (!variant) {
          status.textContent = 'Unavailable';
          status.hidden = false;
          row.classList.add('is-unavailable');
        } else if (!this.isVariantPurchasable(variant)) {
          status.textContent = 'Out of stock';
          status.hidden = false;
          row.classList.add('is-unavailable');
        } else {
          status.textContent = '';
          status.hidden = true;
          row.classList.remove('is-unavailable');
        }
      }
    }

    findVariantImageForOption(optionIndex, value) {
      if (!value) return null;
      const key = `option${optionIndex + 1}`;
      const match = (this.config.variants || []).find(
        (v) => v[key] === value && v.featured_image
      );
      return match?.featured_image || null;
    }

    collectPerItemCartLines(properties = {}) {
      const counts = new Map();
      (this.itemSelections || []).forEach((sel) => {
        const id = Number(sel?.variantId);
        if (!id) return;
        const variant = this.getVariantById(id);
        if (!this.isVariantPurchasable(variant)) return;
        counts.set(id, (counts.get(id) || 0) + 1);
      });

      return [...counts.entries()].map(([id, quantity]) => ({
        id,
        quantity,
        properties: { ...properties },
      }));
    }

    validatePerItemSelections() {
      if (!this.usesPerItemVariants() || this.getPackQuantity() < 2) return true;
      if (!this.itemSelections?.length) return false;
      return this.itemSelections.every((sel) => {
        const variant = this.getVariantById(sel?.variantId);
        return this.isVariantPurchasable(variant);
      });
    }

    toggleNativeQuantity() {
      if (!this.config.hideQuantitySelector) return;
      const qty = document.querySelector('.product-form__quantity, quantity-input, .quantity');
      if (qty) qty.style.display = 'none';
      const block = document.querySelector('[data-block-type="quantity_selector"]');
      if (block) block.style.display = 'none';
    }

    syncQuantityField() {
      if (this.config.bundleType === 'quantity_break') {
        const tier = this.getSelectedTier();
        if (tier) this.setFormQuantity(tier.quantity);
      }
    }

    setFormQuantity(qty) {
      if (!this.productForm) return;
      let input = this.productForm.querySelector('[name="quantity"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        this.productForm.appendChild(input);
      }
      input.value = String(qty);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    applyMultipackVariant() {
      if (this.config.discountMode !== 'multipack_variants') return;
      const tier = this.getSelectedTier();
      if (!tier?.variantId || !this.productForm) return;
      const idInput = this.productForm.querySelector('[name="id"]');
      if (idInput) {
        idInput.value = String(tier.variantId);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setFormQuantity(1);
    }

    calcTierPrice(tier, unitPrice) {
      const qty = Number(tier.quantity) || 1;
      const full = unitPrice * qty;
      let perUnit = unitPrice;
      let total = full;
      const discountValue = Number(tier.discountValue) || 0;
      const shopUnitPrice = this.getShopCurrencyUnitPrice(unitPrice);

      if (tier.discountType === 'percentage') {
        const off = shopifyPercentOffCents(unitPrice, discountValue);
        perUnit = Math.max(0, unitPrice - off);
        total = perUnit * qty;
      } else if (tier.discountType === 'fixed') {
        if (shopUnitPrice > 0) {
          const pct = Math.min(95, Math.max(0, (discountValue / shopUnitPrice) * 100));
          const off = shopifyPercentOffCents(unitPrice, pct);
          perUnit = Math.max(0, unitPrice - off);
        } else {
          perUnit = Math.max(0, unitPrice - toPresentmentCents(discountValue, this.config?.shopCurrency));
        }
        total = perUnit * qty;
      } else if (tier.discountType === 'custom_price') {
        const shopFull = shopUnitPrice * qty;
        if (shopUnitPrice > 0 && shopFull > 0) {
          total = Math.round(full * (discountValue / shopFull));
        } else {
          total = toPresentmentCents(discountValue, this.config?.shopCurrency);
        }
        perUnit = qty > 0 ? Math.round(total / qty) : total;
      }

      return { perUnit, total, full, save: Math.max(0, full - total) };
    }

    updateDisplays() {
      const unitPrice = this.unitPrice;

      if (this.config.bundleType === 'quantity_break') {
        this.querySelectorAll('[data-pb-tier]').forEach((el) => {
          const tier = this.config.tiers[Number(el.dataset.pbTier)];
          if (!tier) return;
          const calc = this.calcTierPrice(tier, unitPrice);
          const priceEl = el.querySelector('[data-pb-tier-price]');
          const compareEl = el.querySelector('[data-pb-tier-compare]');
          const saveEl = el.querySelector('[data-pb-tier-save]');
          const perEl = el.querySelector('[data-pb-tier-per]');
          if (priceEl) priceEl.textContent = moneyFormat(calc.total);
          if (compareEl) {
            if (calc.save > 0) {
              compareEl.textContent = moneyFormat(calc.full);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
          if (perEl) {
            perEl.textContent = (this.config.onlyPerLabel || 'Only [price] per [term]')
              .replaceAll('[price]', moneyFormat(calc.perUnit))
              .replaceAll('[term]', this.config.productTerm || 'item');
          }
          if (saveEl) {
            if (calc.save > 0 && calc.full > 0) {
              const pct =
                tier.discountType === 'percentage' && Number(tier.discountValue) > 0
                  ? Math.round(Number(tier.discountValue))
                  : Math.round((calc.save / calc.full) * 100);
              saveEl.textContent = (this.config.saveLabel || 'SAVE [pct]%')
                .replaceAll('[pct]', String(pct))
                .replaceAll('{{pct}}', String(pct));
              saveEl.hidden = false;
            } else {
              saveEl.hidden = true;
            }
          }
        });
      }

      if (this.config.bundleType === 'fbt' || this.config.bundleType === 'mix_match') {
        const { full, total, save } = this.calcGroupTotals(unitPrice);
        const totalEl = this.querySelector('[data-pb-total]');
        const saveEl = this.querySelector('[data-pb-total-save]');
        if (totalEl) totalEl.textContent = moneyFormat(total);
        if (saveEl) {
          if (save > 0) {
            saveEl.textContent = (this.config.youSaveLabel || 'You save [amount]')
              .replaceAll('[amount]', moneyFormat(save))
              .replaceAll('{{amount}}', moneyFormat(save));
            saveEl.hidden = false;
          } else {
            saveEl.hidden = true;
          }
        }
        void full;
      }

      if (this.config.bundleType === 'bxgy') {
        const buy = Number(this.config.bxgy.buyQty) || 2;
        const get = Number(this.config.bxgy.getQty) || 1;
        const full = unitPrice * (buy + get);
        const total = unitPrice * buy;
        const totalEl = this.querySelector('[data-pb-total]');
        const saveEl = this.querySelector('[data-pb-total-save]');
        if (totalEl) totalEl.textContent = moneyFormat(total);
        if (saveEl) {
          saveEl.textContent = (this.config.youSaveLabel || 'You save [amount]')
            .replaceAll('[amount]', moneyFormat(full - total))
            .replaceAll('{{amount}}', moneyFormat(full - total));
        }
      }

      this.emitSelectionChange();
    }

    calcGroupTotals(unitPrice) {
      const pct = Number(this.config.groupDiscountPercent) || 0;
      const prices = [];

      if (this.config.bundleType === 'fbt') {
        prices.push(Number(unitPrice) || 0);
      }

      const items = this.querySelectorAll(
        this.config.bundleType === 'fbt' ? '[data-pb-fbt-item]' : '[data-pb-mix-item]'
      );
      items.forEach((el) => {
        const check = el.querySelector('input[type="checkbox"]');
        if (!check?.checked) return;
        prices.push(Number(el.dataset.price) || 0);
      });

      const full = prices.reduce((sum, price) => sum + price, 0);
      const applyDiscount =
        pct > 0 &&
        (this.config.bundleType !== 'mix_match' ||
          prices.length >= (Number(this.config.mixMinQty) || 2));

      const total = applyDiscount
        ? prices.reduce((sum, price) => sum + (price - shopifyPercentOffCents(price, pct)), 0)
        : full;

      return { full, total, save: Math.max(0, full - total) };
    }

    interceptSubmit() {
      if (!this.productForm) return;
      this.productForm.addEventListener(
        'submit',
        (evt) => {
          if (this._skipIntercept) return;
          const type = this.config.bundleType;
          if (type === 'quantity_break') {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.addSelectedBundleToCart();
            return;
          }
          if (type === 'fbt' || type === 'mix_match') {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.addGroupBundle();
            return;
          }
          if (type === 'bxgy') {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.addBxgyWithCode();
          }
        },
        true
      );
    }

    getActiveDiscountCode() {
      const tier = this.getSelectedTier();
      return (tier?.discountCode || this.config.discountCode || '').trim();
    }

    async applyDiscountCode(code) {
      if (!code) return false;
      let applied = false;
      try {
        await fetch(`/discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          credentials: 'same-origin',
        });
        applied = true;
      } catch (e) {
        console.warn('[product-bundles] /discount/ failed', e);
      }
      try {
        const res = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: code }),
        });
        if (res.ok) applied = true;
      } catch (e) {
        console.warn('[product-bundles] cart/update discount failed', e);
      }
      return applied;
    }

    getSubmitButton() {
      return (
        this.productForm?.closest('product-form')?.querySelector('[type="submit"]') ||
        this.productForm?.querySelector('[type="submit"]')
      );
    }

    setLoading(loading) {
      const btn = this.getSubmitButton();
      if (!btn) return;
      if (loading) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
      } else {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
      }
    }

    openCartDrawer() {
      if (window.jQuery) {
        jQuery(document).trigger('adev:cart:drawer:refresh');
        jQuery(document).trigger('adev:open:drawer', 'cart-drawer');
      }
    }

    async addToCartItems(items) {
      const config = fetchConfig('javascript');
      config.body = JSON.stringify({
        items,
        sections: 'adev-cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      });
      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Cart error');
      return data;
    }

    async addSelectedBundleToCart() {
      if (this.config.bundleType === 'fbt' || this.config.bundleType === 'mix_match') {
        return this.addGroupBundle();
      }
      if (this.config.bundleType === 'bxgy') {
        return this.addBxgyWithCode();
      }
      return this.addQuantityBreakWithCode();
    }

    async addQuantityBreakWithCode() {
      this.setLoading(true);
      try {
        if (!this.validatePerItemSelections()) {
          throw new Error('Please choose an available option for each item in the bundle.');
        }

        this.applyMultipackVariant();
        const tier = this.getSelectedTier();
        const code = this.getActiveDiscountCode();
        const needsCode =
          this.config.discountMode === 'discount_code' ||
          (Number(tier?.quantity) > 1 && tier?.discountType && tier.discountType !== 'none');

        if (code) {
          await this.applyDiscountCode(code);
        } else if (needsCode && this.config.discountMode === 'discount_code') {
          console.warn(
            '[product-bundles] Nenhum código de desconto configurado. Crie o código no Admin Shopify e cole no bloco Product Bundles.'
          );
        }

        const properties = {
          _bundle_type: 'quantity_break',
          _bundle_tier: String(tier?.quantity || 1),
          ...(code ? { _bundle_discount_code: code } : {}),
        };

        let items;
        if (this.usesPerItemVariants() && this.getPackQuantity() >= 2 && this.itemSelections.length) {
          items = this.collectPerItemCartLines(properties);
          if (!items.length) {
            throw new Error('Please choose an available option for each item in the bundle.');
          }
        } else {
          const qty =
            this.config.discountMode === 'multipack_variants' ? 1 : Number(tier?.quantity) || 1;
          const idInput = this.productForm.querySelector('[name="id"]');
          const variantId = Number(
            (this.config.discountMode === 'multipack_variants' && tier?.variantId
              ? tier.variantId
              : idInput?.value) || this.config.variantId
          );
          items = [{ id: variantId, quantity: qty, properties }];
        }

        await this.addToCartItems(items);
        if (code) await this.applyDiscountCode(code);
        this.openCartDrawer();
      } catch (e) {
        console.error(e);
        alert(e.message || 'Could not add to cart');
      } finally {
        this.setLoading(false);
      }
    }

    async addBxgyWithCode() {
      this.setLoading(true);
      try {
        if (!this.validatePerItemSelections()) {
          throw new Error('Please choose an available option for each item in the bundle.');
        }

        const code = this.getActiveDiscountCode();
        if (code) await this.applyDiscountCode(code);
        const buy = Number(this.config.bxgy.buyQty) || 2;
        const get = Number(this.config.bxgy.getQty) || 1;
        const properties = {
          _bundle_type: 'bxgy',
          _bundle_buy: String(buy),
          _bundle_get: String(get),
        };

        let items;
        if (this.usesPerItemVariants() && this.itemSelections.length >= 2) {
          items = this.collectPerItemCartLines(properties);
          if (!items.length) {
            throw new Error('Please choose an available option for each item in the bundle.');
          }
        } else {
          const idInput = this.productForm.querySelector('[name="id"]');
          const variantId = Number(idInput?.value || this.config.variantId);
          items = [{ id: variantId, quantity: buy + get, properties }];
        }

        await this.addToCartItems(items);
        if (code) await this.applyDiscountCode(code);
        this.openCartDrawer();
      } catch (e) {
        console.error(e);
        alert(e.message || 'Could not add to cart');
      } finally {
        this.setLoading(false);
      }
    }

    collectCheckedItems(selector) {
      const items = [];
      this.querySelectorAll(selector).forEach((el) => {
        const check = el.querySelector('input[type="checkbox"]');
        if (!check?.checked) return;
        const select = el.querySelector('select');
        const id = Number(select?.value || el.dataset.variantId);
        if (!id) return;
        items.push({
          id,
          quantity: 1,
          properties: {
            _bundle_type: this.config.bundleType,
            _bundle_group: this.config.bundleGroupId,
          },
        });
      });
      return items;
    }

    async addGroupBundle() {
      this.setLoading(true);
      try {
        if (this.config.discountMode === 'discount_code' && this.config.discountCode) {
          await this.applyDiscountCode(this.config.discountCode);
        } else if (this.config.discountCode) {
          await this.applyDiscountCode(this.config.discountCode);
        }

        const items = [];
        if (this.config.bundleType === 'fbt') {
          const idInput = this.productForm.querySelector('[name="id"]');
          items.push({
            id: Number(idInput?.value || this.config.variantId),
            quantity: 1,
            properties: {
              _bundle_type: 'fbt',
              _bundle_group: this.config.bundleGroupId,
            },
          });
          items.push(...this.collectCheckedItems('[data-pb-fbt-item]'));
        } else {
          items.push(...this.collectCheckedItems('[data-pb-mix-item]'));
          if (items.length < (Number(this.config.mixMinQty) || 1)) {
            throw new Error(this.config.mixMinError || 'Select more products to unlock the deal.');
          }
        }

        if (!items.length) throw new Error('Select at least one product.');
        await this.addToCartItems(items);
        if (this.config.discountCode) await this.applyDiscountCode(this.config.discountCode);
        this.openCartDrawer();
      } catch (e) {
        console.error(e);
        alert(e.message || 'Could not add to cart');
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get('product-bundles')) {
    customElements.define('product-bundles', ProductBundles);
  }

  document.addEventListener('variant:change', () => {
    document.querySelectorAll('product-bundles').forEach((el) => el.updateDisplays?.());
  });
  document.addEventListener('change', (e) => {
    if (e.target?.matches?.('product-form [name="id"], form[action*="/cart/add"] [name="id"]')) {
      document.querySelectorAll('product-bundles').forEach((el) => {
        el.updateDisplays?.();
        // If pack pickers are showing and user changed main variant, do not overwrite customized rows.
      });
    }
  });
})();
